/**
 * SYSTEM SETTINGS MODULE — States, Distributors & Regions.
 * Manages the per-tenant `sales_states` rows (state -> region -> distributor)
 * that sales-forecasting (and, later, the distribution map / orders) read.
 * Data access is the shared referenceDataService (admin module -> shared).
 */

import { useEffect, useMemo, useState } from "react";
import {
  listSalesStates,
  createSalesState,
  updateSalesState,
  deleteSalesState,
  listSalesStateModes,
  listSalesCounties,
  listTerritoryManagers,
  setTerritoryManager,
} from "../../../shared/utils/referenceDataService.js";
import { getMyPrimaryAccount, listMembers } from "../../../shared/utils/accountService.js";
import { resolveStateToIso, isoToStateFips } from "../../../shared/utils/countyGeo.js";
import CountyModal from "./CountyModal.jsx";

const EMPTY = { stateName: "", region: "", distributor: "" };

export default function StatesSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [add, setAdd] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(EMPTY);
  const [countyModes, setCountyModes] = useState(new Set()); // ISO codes in 'county' mode
  const [countyPainted, setCountyPainted] = useState(new Set()); // ISO codes that actually have counties
  const [nameIso, setNameIso] = useState({});                // state_name -> ISO ('US-CA')
  const [countyFor, setCountyFor] = useState(null);          // { stateName, iso } when modal open
  const [members, setMembers] = useState([]);                // team roster for manager dropdowns
  const [mgrByRegion, setMgrByRegion] = useState({});        // region -> manager_user_id

  async function loadModes() {
    try {
      const [modes, counties] = await Promise.all([
        listSalesStateModes(),
        listSalesCounties().catch(() => []),
      ]);
      const onCodes = modes.filter((m) => m.mode === "county").map((m) => m.state_code);
      setCountyModes(new Set(onCodes));

      // Which of those states have any county actually assigned. County mode with
      // nothing painted has NO effect on a published map (map-public falls back to
      // the whole-state distributor), so the badge has to say so — otherwise this
      // table reads as "split by county" for a state the customer's visitors see
      // painted whole.
      const paintedFips = new Set(
        counties.filter((c) => c.active !== false).map((c) => String(c.county_fips).slice(0, 2))
      );
      const painted = new Set();
      for (const iso of onCodes) {
        const fips = await isoToStateFips(iso).catch(() => null);
        if (fips && paintedFips.has(fips)) painted.add(iso);
      }
      setCountyPainted(painted);
    } catch {
      /* badges are non-critical; ignore */
    }
  }

  // Team roster (for the manager dropdowns) + current territory assignments.
  async function loadTerritoryManagers() {
    try {
      const acct = await getMyPrimaryAccount();
      const [roster, mgrs] = await Promise.all([
        acct?.accountId ? listMembers(acct.accountId) : Promise.resolve([]),
        listTerritoryManagers(),
      ]);
      setMembers(roster || []);
      setMgrByRegion(Object.fromEntries((mgrs || []).map((m) => [m.region, m.manager_user_id])));
    } catch {
      /* manager assignment is optional; ignore load errors */
    }
  }

  async function assignManager(region, userId) {
    setMgrByRegion((m) => ({ ...m, [region]: userId || null }));
    try { await setTerritoryManager(region, userId); }
    catch (e) { setErr(e.message); loadTerritoryManagers(); }
  }

  async function load() {
    setErr(null);
    try {
      setRows(await listSalesStates());
      loadModes();
      loadTerritoryManagers();
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  // Resolve each distinct state name -> ISO (for the by-county badge + modal).
  useEffect(() => {
    let alive = true;
    (async () => {
      const out = {};
      for (const name of new Set(rows.map((r) => r.state_name).filter(Boolean))) {
        out[name] = await resolveStateToIso(name);
      }
      if (alive) setNameIso(out);
    })();
    return () => { alive = false; };
  }, [rows]);

  // Distinct distributor names across the account (offered in the county modal).
  const distributorOptions = useMemo(
    () => [...new Set(rows.map((r) => (r.distributor || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  // Distinct territories (= regions) across the account, for manager assignment.
  const regions = useMemo(
    () => [...new Set(rows.map((r) => (r.region || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  async function run(fn) {
    setBusy(true);
    setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!add.stateName.trim()) { setErr("State name is required."); return; }
    if (!add.distributor.trim()) { setErr("Distributor is required."); return; }
    await run(async () => {
      await createSalesState(add);
      setAdd(EMPTY);
    });
  }

  function startEdit(r) {
    setEditId(r.id);
    setEditRow({ stateName: r.state_name, region: r.region || "", distributor: r.distributor || "" });
  }

  async function saveEdit(id) {
    if (!editRow.stateName.trim()) { setErr("State name is required."); return; }
    if (!editRow.distributor.trim()) { setErr("Distributor is required."); return; }
    await run(async () => {
      await updateSalesState(id, editRow);
      setEditId(null);
    });
  }

  const cell = { padding: "9px 10px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
  const inputStyle = { margin: 0, width: "100%" };

  return (
    <section className="settings-card wide">
      <h2 style={{ marginTop: 0 }}>States, Distributors &amp; Regions</h2>
      <p className="muted">
        Each state you sell in, its sales region, and the distributor that covers it.
        The Sales Forecast uses these to build its grid.
      </p>

      <form onSubmit={handleAdd} style={{ display: "flex", flexDirection: "row", gap: 10, flexWrap: "wrap", alignItems: "flex-end", margin: "14px 0 18px" }}>
        <label style={{ flex: "1 1 160px", margin: 0 }}>
          State
          <input value={add.stateName} onChange={(e) => setAdd({ ...add, stateName: e.target.value })} placeholder="California" maxLength={120} style={{ margin: "4px 0 0" }} />
        </label>
        <label style={{ flex: "1 1 140px", margin: 0 }}>
          <span>Region <span className="muted">(optional)</span></span>
          <input value={add.region} onChange={(e) => setAdd({ ...add, region: e.target.value })} placeholder="West" maxLength={120} style={{ margin: "4px 0 0" }} />
        </label>
        <label style={{ flex: "1 1 180px", margin: 0 }}>
          Distributor
          <input value={add.distributor} onChange={(e) => setAdd({ ...add, distributor: e.target.value })} placeholder="Acme Beverage Co." maxLength={160} style={{ margin: "4px 0 0" }} />
        </label>
        <button className="btn" type="submit" disabled={busy || !add.stateName.trim() || !add.distributor.trim()} style={{ flex: "0 0 auto", alignSelf: "flex-end" }}>Add state</button>
      </form>

      {err && <p className="error">{err}</p>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No states yet — add your first one above.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={cell}>State</th>
                <th style={cell}>Region</th>
                <th style={cell}>Distributor</th>
                <th style={cell}>Active</th>
                <th style={{ ...cell, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const editing = editId === r.id;
                return (
                  <tr key={r.id}>
                    <td style={cell}>
                      {editing
                        ? <input value={editRow.stateName} onChange={(e) => setEditRow({ ...editRow, stateName: e.target.value })} style={inputStyle} />
                        : (
                          <>
                            {r.state_name}
                            {nameIso[r.state_name] && countyModes.has(nameIso[r.state_name]) && (
                              countyPainted.has(nameIso[r.state_name])
                                ? <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>· by county</span>
                                : (
                                  <span
                                    className="warning"
                                    style={{ fontSize: 12, marginLeft: 8 }}
                                    title={`${r.state_name} is set to distribute by county but no counties are assigned, so maps still use its whole-state distributor. Open Counties to assign them.`}
                                  >
                                    · by county — none assigned
                                  </span>
                                )
                            )}
                          </>
                        )}
                    </td>
                    <td style={cell}>
                      {editing
                        ? <input value={editRow.region} onChange={(e) => setEditRow({ ...editRow, region: e.target.value })} style={inputStyle} />
                        : (r.region || <span className="muted">—</span>)}
                    </td>
                    <td style={cell}>
                      {editing
                        ? <input value={editRow.distributor} onChange={(e) => setEditRow({ ...editRow, distributor: e.target.value })} style={inputStyle} />
                        : (r.distributor || <span className="muted">—</span>)}
                    </td>
                    <td style={cell}>
                      <input
                        type="checkbox"
                        checked={r.active}
                        disabled={busy}
                        onChange={(e) => run(() => updateSalesState(r.id, { active: e.target.checked }))}
                        title={r.active ? "Active" : "Inactive"}
                      />
                    </td>
                    <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                      {editing ? (
                        <>
                          <button className="btn" type="button" disabled={busy} onClick={() => saveEdit(r.id)} style={{ marginRight: 8 }}>Save</button>
                          <button type="button" className="secondary" disabled={busy} onClick={() => setEditId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="link"
                            disabled={busy || !nameIso[r.state_name]}
                            title={nameIso[r.state_name] ? "Set distribution by county" : "No county data for this state name"}
                            onClick={() => setCountyFor({ stateName: r.state_name, iso: nameIso[r.state_name] })}
                            style={{ marginRight: 12 }}
                          >
                            Counties
                          </button>
                          <button type="button" className="link" disabled={busy} onClick={() => startEdit(r)} style={{ marginRight: 12 }}>Edit</button>
                          <button
                            type="button"
                            className="link"
                            style={{ color: "var(--error)" }}
                            disabled={busy}
                            onClick={() => { if (window.confirm(`Delete ${r.state_name}?`)) run(() => deleteSalesState(r.id)); }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Territory managers (one Team user per region) ─────────────────── */}
      {!loading && (
        <div style={{ marginTop: 30 }}>
          <h3 style={{ fontSize: 15, margin: "0 0 4px" }}>Territory managers</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Assign one manager to each territory (region). They oversee the states in that region.
          </p>
          {regions.length === 0 ? (
            <p className="muted">Add a <strong>Region</strong> to your states above to create territories you can assign managers to.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left" }}>
                    <th style={cell}>Territory</th>
                    <th style={cell}>Manager</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.map((region) => (
                    <tr key={region}>
                      <td style={cell}>{region}</td>
                      <td style={cell}>
                        <select
                          value={mgrByRegion[region] || ""}
                          disabled={busy}
                          onChange={(e) => assignManager(region, e.target.value)}
                          style={{ width: "auto", minWidth: 220, margin: 0 }}
                        >
                          <option value="">Unassigned</option>
                          {members.map((m) => (
                            <option key={m.user_id} value={m.user_id}>{m.email}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {countyFor && (
        <CountyModal
          stateName={countyFor.stateName}
          initialMode={countyFor.iso && countyModes.has(countyFor.iso) ? "county" : "state"}
          distributorOptions={distributorOptions}
          onClose={() => setCountyFor(null)}
          onChanged={loadModes}
        />
      )}
    </section>
  );
}
